"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import Button from "@/components/common/Button";

const STORAGE_KEY = "ecq_cookie_consent";

type ConsentValue = "accepted" | "rejected";

type ConsentModeGtagFn = (
  command: "consent",
  action: "default" | "update",
  params: Record<string, unknown>
) => void;

type DataLayerEvent = Record<string, unknown> | IArguments;

function applyGoogleConsent(value: ConsentValue) {
  // Best-effort: works for GTM/GA Consent Mode. Safe if GTM isn't present yet.
  const w = window as unknown as {
    dataLayer?: DataLayerEvent[];
    gtag?: ConsentModeGtagFn;
  };

  w.dataLayer = w.dataLayer || [];

  const gtag: ConsentModeGtagFn =
    w.gtag ||
    ((command, action, params) => {
      // Fallback: queue calls until GTM/gtag is ready.
      // Enqueue a structured form for GTM listeners.
      w.dataLayer?.push({ command, action, params });
    });

  // Only enable analytics when accepted. Keep ads denied by default.
  gtag("consent", "update", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: value === "accepted" ? "granted" : "denied",
    functionality_storage: "granted",
    security_storage: "granted",
    personalization_storage: "denied",
  });

  // Helpful for GTM triggers if you want them later.
  w.dataLayer.push({
    event: "cookie_consent_update",
    cookie_consent: value,
  });
}

/**
 * Cookie consent banner (lightweight).
 *
 * Why: required for compliance + improves user trust. Also enables cookie-gating later if desired.
 * Note: This banner stores the user's choice in localStorage.
 */
export default function CookieConsentBanner() {
  const [isMounted, setIsMounted] = useState(false);
  const [visible, setVisible] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setIsMounted(true);
    try {
      const value = window.localStorage.getItem(STORAGE_KEY) as ConsentValue | null;
      if (value) {
        // Persisted choice: apply it immediately on each page load.
        applyGoogleConsent(value);
      }
      const shouldShow = !value;
      setVisible(shouldShow);
      if (shouldShow) {
        // Trigger enter animation after initial paint.
        requestAnimationFrame(() => setOpen(true));
      }
    } catch {
      // If storage is blocked, show banner (best-effort).
      setVisible(true);
      requestAnimationFrame(() => setOpen(true));
    }
  }, []);

  const setConsent = (value: ConsentValue) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, value);
    } catch {
      // ignore
    }
    applyGoogleConsent(value);
    setOpen(false);
    window.setTimeout(() => setVisible(false), 250);
  };

  if (!isMounted || !visible) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      className="fixed bottom-4 left-4 right-4 z-[9998]"
    >
      {/* 6xl, not 5xl: at 5xl the text column topped out at 674px against copy
          that needs 681px, so the last phrase always wrapped. 6xl gives it
          804px and the sentence sits on one line from ~1061px up. */}
      <div className="max-w-6xl mx-auto">
        <div
          className={[
            // Base
            "relative overflow-hidden rounded-[16px] border border-white/10 bg-[#202328]/95 backdrop-blur-md shadow-[0_20px_60px_rgba(0,0,0,0.45)]",
            // Enter/exit animation
            "motion-safe:transition-all motion-safe:duration-300 motion-safe:ease-out",
            "motion-reduce:transition-none",
            open ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4",
          ].join(" ")}
        >
          {/* subtle cyber accent */}
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-primary/10 via-transparent to-primary/5" />

          {/* Two shapes only: copy-over-buttons, or one row. The row needs the
              276px button pair AND 681px of copy plus gap and padding — 1029px
              of banner, i.e. a ~1061px viewport — so it turns on at xl, not md.
              At md the row squeezed the copy to 386px (2 lines) and at lg to
              642px, still 39px short, which is what pushed "Learn more." onto a
              line of its own. Stacked, the copy has the full width and stays on
              one line from 768px up. */}
          <div className="relative p-4 sm:p-5 md:p-6 flex flex-col xl:flex-row xl:items-center gap-4 xl:gap-6">
            <div className="flex-1">
              <p className="text-white font-semibold tracking-wide">We use cookies</p>
              <p className="text-text-default text-sm leading-6 mt-1">
                We use cookies to keep the site secure and working properly, and to improve your
                experience.{" "}
                {/* nowrap so the link never breaks mid-phrase: at 1280 the copy
                    needs 681px and the text column had 674, which split it into
                    "Learn" / "more" across two lines. As one token it either
                    fits or moves down whole. */}
                {/* /cookies, not /privacy: V1 pointed at privacy.html because
                    that page carried a "Cookies and Tracking Technologies"
                    section, but this build moved that content into its own
                    Cookie Policy page. Privacy.tsx no longer contains the word
                    "cookie" at all, while Cookie.tsx has the "Your Cookies
                    Choices" section this link is meant to reach. */}
                <span className="whitespace-nowrap">
                  <Link href="/cookies" className="text-primary hover:underline">
                    Learn more
                  </Link>
                  .
                </span>
              </p>
            </div>

            {/* Kept on one row at every width: in a stacked (flex-col) row the
                pill has no intrinsic width cap, so align-items:stretch blew
                Accept out to the full banner width while Reject stayed at
                w-max. Both buttons are 40px tall — the compact .ecq-cta--sm
                pill, not the 58px hero one, which dwarfed Reject. */}
            <div className="flex flex-row flex-wrap items-center gap-3">
              {/* Reject is the same V1 pill as Accept in its neutral colour
                  (--ghost), not the legacy rounded-[8px] secondary box, which
                  read as a foreign widget beside the pill. Sharing the pill
                  means identical geometry, casing and typography for free —
                  only the accent colour differs. */}
              <Button
                primary
                themeProp="dark"
                onClick={() => setConsent("rejected")}
                customClassName="ecq-cta--sm ecq-cta--ghost whitespace-nowrap"
              >
                Reject
              </Button>
              <Button
                primary
                themeProp="dark"
                onClick={() => setConsent("accepted")}
                customClassName="ecq-cta--sm whitespace-nowrap"
              >
                Accept
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
